import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { fetchInvoiceStopperRules } from './fetchInvoiceStopperRules';
import {
  type InvoiceStopperRulesQueryParameters,
  type InvoiceStopperRulesResponse,
} from './types';

/**
 * Get the query options for fetching invoice stopper rules.
 * @param options - Optional configuration for the query.
 * @param queryParameters - The search parameters for fetching the invoice stopper rules.
 * @returns The query options.
 */
export const getInvoiceStopperRulesQueryOptions = ({
  options = {},
  queryParameters,
}: {
  options?: Omit<
    UseQueryOptions<InvoiceStopperRulesResponse>,
    'queryKey' | 'queryFn'
  >;
  queryParameters: InvoiceStopperRulesQueryParameters;
}) =>
  queryOptions({
    async queryFn({ queryKey }) {
      const queryParameters = (
        queryKey as ReturnType<
          typeof queryKeysInvoiceReprocessing.getStopperRules
        >
      )[3];

      return fetchInvoiceStopperRules(queryParameters);
    },
    queryKey: queryKeysInvoiceReprocessing.getStopperRules(queryParameters),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query based custom hook for getting a invoice stopper rule with a given ID.
 * @param options - Optional configuration for the query.
 * @param queryParameters - The search parameters for fetching the invoice stopper rules.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/get_invoice_stopper_rules_vadmin_invoice_stopper_rule_get
 */
export const useQueryInvoiceStopperRules = ({
  options = {},
  queryParameters = {},
}: {
  options?: Omit<
    UseQueryOptions<InvoiceStopperRulesResponse>,
    'queryKey' | 'queryFn'
  >;
  queryParameters: Partial<InvoiceStopperRulesQueryParameters>;
}) => {
  const queryClient = useQueryClient();

  const nextPageOffset =
    (queryParameters?.offset ?? 0) +
    (queryParameters?.limit ?? PAGINATION_PAGE_SIZE_DEFAULT);

  void queryClient.prefetchQuery(
    getInvoiceStopperRulesQueryOptions({
      options,
      queryParameters: {
        ...queryParameters,
        offset: nextPageOffset,
      },
    }),
  );

  return useQuery({
    ...getInvoiceStopperRulesQueryOptions({
      options,
      queryParameters,
    }),
    placeholderData: keepPreviousData,
  });
};
